import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { cn } from '~/lib/utils';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

const variantStyles: Record<AlertVariant, string> = {
  info: 'border-blue-400/40 bg-blue-500/10 text-blue-600 dark:text-blue-300',
  success: 'border-green-400/40 bg-green-500/10 text-green-600 dark:text-green-300',
  warning: 'border-amber-400/40 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  error: 'border-red-400/40 bg-red-500/10 text-red-600 dark:text-red-300',
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
}

export function Alert({ className, variant = 'info', title, children, ...props }: AlertProps) {
  return (
    <div
      className={cn('rounded-lg border p-4 text-sm', variantStyles[variant], className)}
      role="alert"
      {...props}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={title ? 'mt-1' : undefined}>{children}</div> : null}
    </div>
  );
}

export function Spinner({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <span
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary',
        className
      )}
      style={{ width: size, height: size }}
      aria-label="Loading"
    />
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} />;
}

export function ProgressBar({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full bg-gradient-to-r from-primary to-accent"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  variant?: AlertVariant;
}

interface ToastContextValue {
  toasts: ToastMessage[];
  showToast: (toast: Omit<ToastMessage, 'id'>) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, ...toast }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id != id));
  }, []);

  const value = useMemo(
    () => ({ toasts, showToast, dismissToast }),
    [dismissToast, showToast, toasts]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col gap-3 p-4 sm:inset-x-auto sm:right-4 sm:w-full sm:max-w-sm">
        {toasts.map((toast) => (
          <Alert
            key={toast.id}
            variant={toast.variant ?? 'info'}
            title={toast.title}
            className="pointer-events-auto"
          >
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-foreground">{toast.description}</p>
              <button
                type="button"
                className="text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
                onClick={() => dismissToast(toast.id)}
              >
                Close
              </button>
            </div>
          </Alert>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
